/**
 * Filter schema and URL state for Sewing Hourly Board.
 *
 * Mirrors the server's `filters:` block, which is authoritative — the server rejects anything this
 * file gets wrong, it does not quietly widen the answer.
 */
import { z } from 'zod';

import { FACTORY_OPTIONS, type FactoryCode } from '@/lib/factories';
import { liveOptionsFor, type FilterSchema } from '@/lib/filters';

const live = liveOptionsFor('sewing-hourly-board');

/**
 * The work day. Legacy defaulted to today with `new Date().toISOString()` and re-derived it on
 * every auto-refresh, so a board left open rolled onto the new day by itself. Plain today, with no
 * shift rollover: this screen has no equivalent of the sewing boards' 09:00 rule, and borrowing one
 * would silently show a different day than the old screen did.
 */
export function defaultDate(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export interface SewingHourlyBoardFilterValues {
  tran_date: string;
  factory: FactoryCode;
  building: string;
  floor: string;
}

/**
 * Building and floor options are fetched from the server (`options:` in report.yaml) rather than
 * listed here — §5.4 forbids a hardcoded client list, because it rots and drifts between reports.
 * Both cascade off factory, and floor additionally off building, which is the same narrowing the
 * legacy JSP did in the browser against a permission-scoped dictionary.
 */
export function sewingHourlyBoardFilters(factory: FactoryCode): FilterSchema {
  return [
    {
      type: 'select',
      name: 'factory',
      label: 'Factory',
      required: true,
      defaultValue: factory,
      options: FACTORY_OPTIONS,
    },
    { type: 'date', name: 'tran_date', label: 'Work date', required: true, defaultValue: defaultDate },
    {
      type: 'multi-select',
      name: 'building',
      label: 'Building',
      anyLabel: 'All buildings',
      options: live('building'),
    },
    {
      type: 'multi-select',
      name: 'floor',
      label: 'Floor',
      anyLabel: 'All floors',
      options: live('floor'),
    },
  ];
}

/**
 * Which output the grid shows. A declared server filter: it chooses which measure is displayed,
 * not which lines are in the answer, so both families come back on every request and switching
 * costs no round trip — the same thing the legacy JS did by re-rendering `window.lastTableData`.
 *
 * EINS is the default because the legacy JS initialised `currentOutputType` to EINS and then
 * overwrote the JSP select with it, so EINS is what a reader actually saw on load.
 */
export const METRIC_PARAM = 'metric';
const metricSchema = z.enum(['EINS', 'SFTS']);
export type Metric = z.infer<typeof metricSchema>;

export function parseMetric(raw: string | null): Metric {
  const parsed = metricSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'EINS';
}

export const METRIC_TABS: readonly { value: Metric; label: string; hint: string }[] = [
  { value: 'EINS', label: 'Endline', hint: 'Pieces passing end-of-line QC' },
  { value: 'SFTS', label: 'Sewing', hint: 'Pieces reported off the sewing line' },
];

/**
 * Legacy's `AUTO_REFRESH_INTERVAL` — 15 minutes, and always on once a search has run. Kept rather
 * than aligned to the sewing boards' 120s: this screen reads hourly buckets, so a two-minute poll
 * would be fifteen requests that cannot change the answer.
 */
export const REFRESH_INTERVAL_MS = 15 * 60 * 1000;
